// @flow @format
import BlocksWriter from './writer';
import Api from 'lib/blocks-api';
import FileStore, {
	DOWNLOAD_SETUP_STATUS,
	DOWNLOAD_PAUSING_STATUS,
	DOWNLOAD_PAUSED_STATUS,
	DOWNLOAD_COMPLETED_STATUS,
	DOWNLOAD_RUNNING_STATUS,
	DOWNLOAD_FAILED_STATUS,
	DOWNLOAD_RETRY_STATUS,
} from './store';
import {sleep} from './util';

import type {DownloadRecord, DownloadRecordStatus} from './store';

export class Block {
	static blockSize = 1024 * 1000;

	offset: number;
	position: number;
	done: boolean;

	constructor(position: number) {
		this.position = position;
		this.offset = position * Block.blockSize;
		this.done = false;
	}
}

const STATUS_STOPPED = 'stopped';
const STATUS_RUNNING = 'running';
const STATUS_DONE = 'done';

const STATUSES = {
	[STATUS_DONE]: STATUS_DONE,
	[STATUS_RUNNING]: STATUS_RUNNING,
	[STATUS_STOPPED]: STATUS_STOPPED,
};

type Status = $Keys<typeof STATUSES>;

// Base class to implement FileDownloader and FolderDownloader, this class should not be used.
export class Downloader {
	onSuccess: (download: DownloadRecord) => void;
	onChange: (download: DownloadRecord) => void;

	api: Api;
	writer: BlocksWriter;
	database: FileStore;
	download: DownloadRecord;

	// private variables
	_status: Status;

	constructor(
		download: DownloadRecord,
		dirEntry: FileSystemDirectoryEntry,
		database: FileStore,
		api: Api
	) {
		this.download = download;
		this.database = database;
		this.api = api;
		this.writer = new BlocksWriter(dirEntry, download.id);
		this.download.status = DOWNLOAD_PAUSED_STATUS;

		this._status = STATUS_STOPPED;
	}

	async publishSuccess() {
		if (!this.onSuccess) {
			console.warn(
				'Warning: Downloader.publishSuccess called without a subscriber'
			);
			return;
		}

		this.onSuccess(this.download);
	}

	async publishUpdate() {
		try {
			this.download.updatedAt = new Date();
			await this.database.updateDownload(this.download);
		} catch (error) {
			console.error(`Error: failed to update download, ${error.message}`);
			throw error;
		}

		if (!this.onChange) {
			console.warn('Warning: downloader publish update to no subscriber');
			return;
		}

		this.onChange(this.download);
	}

	// init initialize a dowloader this should be called before any operation
	// since this method must take all associated resources.
	async init() {
		throw new Error('Error: downloader init method not implemented');
	}

	async start() {
		throw new Error('Error: downloader start method not implemented');
	}

	async cancel() {
		throw new Error('Error: downloader cancel method not implemented');
	}

	async fetchWriteLoop() {
		throw new Error(
			'Error: downloader fetchWriteLoop method not implemented'
		);
	}

	async remove() {
		try {
			await this.writer.deleteFile();
			await this.database.removeDownload(this.download.id);
		} catch (error) {
			console.error(`Error: failed to remove download ${error.message}`);
		}
	}

	async stop() {
		if (this._status === STATUS_DONE || this._status === STATUS_STOPPED) {
			return;
		}
		this._status = STATUS_STOPPED;
		this.download.status = DOWNLOAD_PAUSING_STATUS;
		await this.publishUpdate();
	}

	async fail(error: Error) {
		console.error(`Error: unexpected download failure, ${error.message}`);

		this._status = STATUS_STOPPED;
		this.download.status = DOWNLOAD_FAILED_STATUS;

		await this.publishUpdate();
	}

	async updateDownloadStatus(
		status: DownloadRecordStatus,
		done: boolean = false
	): Promise<boolean> {
		try {
			this.download.status = status;
			await this.publishUpdate();

			if (done) {
				await this.publishSuccess();
			}

			return true;
		} catch (error) {
			this.fail(error);

			return false;
		}
	}

	hasStatus(status: Status): boolean {
		return this._status === status;
	}

	setStatus(status: Status) {
		this._status = status;
	}
}

export class FileDownloader extends Downloader {
	_index: number; // keeps track of the next position to fetch
	_positions: number[]; // positions of the blocks to fetch
	_activeWorkers: number; // keeps track of the number of workers running.
	_numWorkers: number; // number of workers to use.

	constructor(
		download: DownloadRecord,
		dirEntry: FileSystemDirectoryEntry,
		database: FileStore,
		api: Api,
		numWorkers: number = 3
	) {
		super(download, dirEntry, database, api);

		this._index = 0;
		this._activeWorkers = 0;
		this._positions = [];
		this._numWorkers = numWorkers;
	}

	async init() {
		if (!await this.updateDownloadStatus(DOWNLOAD_PAUSED_STATUS)) {
			return;
		}

		// if the download is already initialized populate positions.
		if (this.download.initialized) {
			for (let i = 0; i < this.download.numBlocks; i++) {
				if (this.download.blockStatus[i]) {
					continue;
				}
				this._positions.push(i);
			}
		}

		// if the download doesn't have a file already we should create a new one.
		if (!this.download.hasFile) {
			try {
				await this.writer.createFile(this.download.size);

				this.download.diskSize = this.download.size;
				this.download.hasFile = true;

				await this.publishUpdate();
			} catch (error) {
				this.fail(error);
				this.writer.deleteFile();

				return;
			}
		}
	}

	async start() {
		this.setStatus(STATUS_RUNNING);

		if (!await this.updateDownloadStatus(DOWNLOAD_SETUP_STATUS)) {
			return;
		}

		// wait until we can request a file download setup correctly.
		while (this.hasStatus(STATUS_RUNNING)) {
			try {
				await this.api.setupFile(
					this.download.repositoryId,
					this.download.id
				);

				break;
			} catch (error) {
				console.warn(
					`Warning: unexpected setup error, ${error.message}`
				);
				await sleep(2000);
			}
		}

		// wait until file download setup is done.
		while (this.hasStatus(STATUS_RUNNING)) {
			try {
				const {status, num_blocks} = await this.api.queryFile(
					this.download.repositoryId,
					this.download.id
				);

				if (status !== 'done') {
					await sleep(500);
					continue;
				}

				if (!this.download.initialized) {
					this._positions = Array.from(
						{length: num_blocks},
						(_, i) => i
					);

					this.download.initialized = true;
					this.download.numBlocks = num_blocks;
					this.download.blockStatus = Array.from(
						{length: num_blocks},
						() => false
					);

					await this.publishUpdate();
				}

				break;
			} catch (error) {
				console.warn(`Warning: file setup is not ready, ${error}`);
				await sleep(2000);
			}
		}

		if (!await this.updateDownloadStatus(DOWNLOAD_RUNNING_STATUS)) {
			return;
		}

		// start all workers
		const numWorkers = Math.min(this._numWorkers, this._positions.length);

		for (let i = 0; i < numWorkers; i++) {
			this.fetchWriteLoop();
			this._activeWorkers++;
		}

		// if application is closed just before we can update the status we
		// can have a completed download without marked it as finished.
		let completed = true;
		for (let i = 0; i < this._positions.length; i++) {
			if (!this.download.blockStatus[this._positions[i]]) {
				completed = false;
				break;
			}
		}

		if (completed && !this.hasStatus(STATUS_DONE)) {
			this.setStatus(STATUS_DONE);
			await this.updateDownloadStatus(DOWNLOAD_COMPLETED_STATUS, true);
		}
	}

	async cancel() {
		this.stop();
	}

	async fetchWriteLoop() {
		if (this._index >= this._positions.length) {
			if (!this.hasStatus(STATUS_DONE)) {
				this.setStatus(STATUS_DONE);
				this.updateDownloadStatus(DOWNLOAD_COMPLETED_STATUS, true);
			}
			return;
		}

		let block = new Block(this._positions[this._index]);
		this._index++;

		while (this.hasStatus(STATUS_RUNNING)) {
			while (this.hasStatus(STATUS_RUNNING)) {
				let blob: Blob;
				let size: number;

				try {
					blob = await this.api.retrieveFile(
						this.download.repositoryId,
						this.download.id,
						block.position
					);

					size = await this.writer.writeBlock(block.offset, blob);
				} catch (error) {
					console.warn(
						`Warning: failed to retrieve and write block ${block.position}`,
						error
					);

					if (
						!await this.updateDownloadStatus(DOWNLOAD_RETRY_STATUS)
					) {
						this._activeWorkers--;
						return;
					}

					await sleep(2000);
					continue;
				}

				try {
					this.download.downloaded += size;
					this.download.blockStatus[block.position] = true;

					if (this.hasStatus(STATUS_RUNNING)) {
						this.download.status = DOWNLOAD_RUNNING_STATUS;
					}

					await this.publishUpdate();
				} catch (error) {
					this._activeWorkers--;
					this.fail(error);
					return;
				}

				break;
			}
			if (
				this.hasStatus(STATUS_STOPPED) ||
				this._index >= this._positions.length
			) {
				break;
			}

			block = new Block(this._positions[this._index]);
			this._index++;
		}

		this._activeWorkers--;

		if (
			this.hasStatus(STATUS_STOPPED) &&
			this._activeWorkers === 0 &&
			this.download.status === DOWNLOAD_PAUSING_STATUS
		) {
			await this.updateDownloadStatus(DOWNLOAD_PAUSED_STATUS);
		}

		let completed = true;
		for (let i = 0; i < this._positions.length; i++) {
			if (!this.download.blockStatus[this._positions[i]]) {
				completed = false;
				break;
			}
		}

		if (completed && !this.hasStatus(STATUS_DONE)) {
			this.setStatus(STATUS_DONE);
			await this.updateDownloadStatus(DOWNLOAD_COMPLETED_STATUS, true);
		}
	}
}

export class FolderDownloader extends Downloader {
	async init() {
		if (!await this.updateDownloadStatus(DOWNLOAD_PAUSED_STATUS)) {
			return;
		}

		if (!this.download.hasFile) {
			try {
				await this.writer.createFile(0);
				this.download.hasFile = true;
				await this.publishUpdate();
			} catch (error) {
				this.fail(error);
			}
		}
	}

	async start() {
		this.setStatus(STATUS_RUNNING);

		if (!await this.updateDownloadStatus(DOWNLOAD_SETUP_STATUS)) {
			return;
		}

		let initialized = this.download.initialized;

		// check if the download is already initialized in the server,
		// * If the download is already initialized but this is a new download,
		//   we need to cancel current download and start a new one.
		// * If we have stored an initialized download but the server not, we
		//   need to cancel current download int the server and reset the stored
		//   download.
		while (this.hasStatus(STATUS_RUNNING)) {
			try {
				const {ok} = await this.api.queryFolder(
					this.download.repositoryId,
					this.download.id
				);

				if (!ok || (!initialized && ok)) {
					await this.api.cancelFolder();

					this.download.initialized = false;
					this.download.downloaded = 0;
					this.download.offset = 0;

					await this.publishUpdate();

					// only here is safe to update this variable.
					initialized = false;
				}

				break;
			} catch (error) {
				console.warn(
					`Warning: setup folder respond with error, ${error.message}`
				);
				await sleep(1000);
			}
		}

		// wait until we can request a folder download setup correctly.
		if (!initialized) {
			while (this.hasStatus(STATUS_RUNNING)) {
				try {
					await this.api.setupFolder(
						this.download.repositoryId,
						this.download.id,
						this.download.includeDeleted
					);
					break;
				} catch (error) {
					console.warn(
						`Warning: failed to setup folder ${error.message}`
					);
					await sleep(1000);
				}
			}
		}

		this.download.initialized = true;
		if (!await this.updateDownloadStatus(DOWNLOAD_RUNNING_STATUS)) {
			return;
		}

		this.fetchWriteLoop();
	}

	async cancel() {
		await this.stop();

		if (
			this.download.status === DOWNLOAD_RETRY_STATUS ||
			this.download.status === DOWNLOAD_RUNNING_STATUS ||
			this.download.status === DOWNLOAD_PAUSING_STATUS
		) {
			const {onChange, onSuccess} = this;

			await new Promise((resolve, reject) => {
				this.onChange = download => {
					onChange && onChange(download);

					if (
						download.status === DOWNLOAD_PAUSED_STATUS ||
						download.status === DOWNLOAD_FAILED_STATUS
					) {
						this.onChange = onChange;
						this.onSuccess = onSuccess;

						resolve();
					}
				};

				this.onSuccess = download => {
					onSuccess && onSuccess(download);

					this.onChange = onChange;
					this.onSuccess = onSuccess;

					resolve();
				};
			});
		}

		try {
			this.download.initialized = false;
			this.download.downloaded = 0;
			this.download.offset = 0;
			this.download.status = DOWNLOAD_PAUSED_STATUS;

			await this.publishUpdate();
		} catch (error) {
			console.error(`Error: failed to cancel download, ${error.message}`);

			this.fail(error);
		}
	}

	async fetchWriteLoop() {
		while (this.hasStatus(STATUS_RUNNING)) {
			// keep trying until we get the next block correctly.
			while (this.hasStatus(STATUS_RUNNING)) {
				let resp: {status: number, blob?: Blob};
				let size: number;

				try {
					resp = await this.api.retrieveFolder(
						this.download.repositoryId,
						this.download.id
					);

					// there is no new block to fetch yet
					if (resp.status === 304) {
						await sleep(500);
						continue;
					}

					// there is no more blocks to fecth
					if (resp.status === 200) {
						this.setStatus(STATUS_DONE);
						break; // finished
					}

					const blob = resp.blob;
					if (!blob) {
						continue;
					}

					size = await this.writer.writeBlock(
						this.download.offset,
						blob
					);

					const ack = await this.api.ackFolder(size);
					if (!ack.ok) {
						continue;
					}
				} catch (error) {
					console.warn(
						`Warning: failed to retrive next block, ${error.message}`
					);

					if (
						!await this.updateDownloadStatus(DOWNLOAD_RETRY_STATUS)
					) {
						return;
					}

					await sleep(1000);
					continue;
				}

				// we write the next block correctly so we can update database safely
				try {
					if (this.hasStatus(STATUS_RUNNING)) {
						this.download.status = DOWNLOAD_RUNNING_STATUS;
					}

					this.download.downloaded += resp.size || 0;
					this.download.offset += size;
					this.download.diskSize = Math.max(
						this.download.offset,
						this.download.diskSize
					);

					await this.publishUpdate();

					break;
				} catch (error) {
					this.fail(error);
					return;
				}
			}
		}

		if (this.hasStatus(STATUS_DONE)) {
			await this.updateDownloadStatus(DOWNLOAD_COMPLETED_STATUS, true);
			return;
		}

		if (this.hasStatus(STATUS_STOPPED)) {
			await this.updateDownloadStatus(DOWNLOAD_PAUSED_STATUS);
		}
	}
}



// WEBPACK FOOTER //
// ./src/lib/downloader/downloader.js